import { db, batchTransact, transactWithRetry } from '@/lib/db'
import { id } from '@instantdb/react'
import { useRef, useEffect } from 'react'
import type { Note, NoteImage, GenerationMeta } from '@/types'

/**
 * 笔记管理 Hook
 * 提供笔记的增删改查功能
 */
export function useNotes(projectId: string, releaseAssetsCallback?: (noteId: string) => Promise<void>) {
  // 查询项目下的所有笔记
  const { data, isLoading, error } = db.useQuery({
    notes: {
      $: {
        where: { project_id: projectId },
      },
    },
  })

  // 使用轻量级缓存，防止数据在更新时短暂消失
  const notesCache = useRef<Note[]>([])
  const rawNotes = (data?.notes || []) as Note[]
  
  useEffect(() => {
    // 只有当新数据不为空时才更新缓存
    if (rawNotes.length > 0) {
      notesCache.current = rawNotes
    }
  }, [rawNotes])

  // 优先使用最新数据，如果为空且缓存有数据则使用缓存
  const notes = rawNotes.length > 0 ? rawNotes : notesCache.current

  // 创建笔记
  const createNote = async (params: {
    title: string
    content: string
    hashtags: string[]
    style: string
    images?: NoteImage[]
    generation_meta?: GenerationMeta  // 新增：生成元信息
  }) => {
    const noteId = id()
    await transactWithRetry(
      db.tx.notes[noteId].update({
        id: noteId,
        project_id: projectId,
        title: params.title,
        content: params.content,
        hashtags: params.hashtags,
        style: params.style,
        status: 'draft',
        images: params.images || [],
        generation_meta: params.generation_meta,  // 保存生成元信息
        created_at: Date.now(),
      })
    )
    return noteId
  }

  // 更新笔记
  const updateNote = async (
    noteId: string,
    updates: Partial<Omit<Note, 'id' | 'project_id' | 'created_at'>>
  ) => {
    await transactWithRetry(db.tx.notes[noteId].update(updates))
  }

  // 删除笔记
  const deleteNote = async (noteId: string) => {
    // 如果提供了释放素材的回调，先释放素材
    if (releaseAssetsCallback) {
      try {
        await releaseAssetsCallback(noteId)
      } catch (error) {
        console.error('[删除笔记] 释放素材失败:', error)
        // 即使释放失败也继续删除笔记
      }
    }
    await transactWithRetry(db.tx.notes[noteId].delete())
  }

  // 批量删除笔记
  const deleteNotes = async (noteIds: string[]) => {
    if (noteIds.length === 0) return
    // 如果提供了释放素材的回调，先批量释放素材
    if (releaseAssetsCallback) {
      try {
        await Promise.all(noteIds.map(id => releaseAssetsCallback(id)))
      } catch (error) {
        console.error('[批量删除笔记] 释放素材失败:', error)
        // 即使释放失败也继续删除笔记
      }
    }
    const txns = noteIds.map((noteId) => db.tx.notes[noteId].delete())
    await batchTransact(txns)
  }

  // 更新笔记状态
  const updateNoteStatus = async (
    noteId: string,
    status: Note['status']
  ) => {
    await transactWithRetry(db.tx.notes[noteId].update({ status }))
  }

  // 更新笔记配图
  const updateNoteImages = async (noteId: string, images: NoteImage[]) => {
    await transactWithRetry(db.tx.notes[noteId].update({ images }))
  }

  // 🔥 批量更新笔记状态（使用分批处理和自动重试）
  const batchUpdateStatus = async (
    updates: Array<{ noteId: string; status: Note['status'] }>
  ) => {
    if (updates.length === 0) return
    const txns = updates.map(({ noteId, status }) =>
      db.tx.notes[noteId].update({ status })
    )
    await batchTransact(txns)
  }

  // 🔥 批量更新笔记配图（使用分批处理和自动重试）
  const batchUpdateImages = async (
    updates: Array<{ noteId: string; images: NoteImage[] }>
  ) => {
    if (updates.length === 0) return
    const txns = updates.map(({ noteId, images }) =>
      db.tx.notes[noteId].update({ images })
    )
    await batchTransact(txns)
  }

  // 🔥 批量更新笔记状态和配图（使用分批处理和自动重试）
  const batchUpdateNotesWithImages = async (
    updates: Array<{
      noteId: string
      status?: Note['status']
      images?: NoteImage[]
    }>
  ) => {
    if (updates.length === 0) return
    const txns = updates.map(({ noteId, status, images }) => {
      const updateData: Partial<Note> = {}
      if (status !== undefined) updateData.status = status
      if (images !== undefined) updateData.images = images
      return db.tx.notes[noteId].update(updateData)
    })
    await batchTransact(txns)
  }

  return {
    notes,
    isLoading,
    error,
    createNote,
    updateNote,
    deleteNote,
    deleteNotes,
    updateNoteStatus,
    updateNoteImages,
    batchUpdateStatus,
    batchUpdateImages,
    batchUpdateNotesWithImages,
  }
}

/**
 * 获取单个笔记
 */
export function useNote(noteId: string) {
  const { data, isLoading, error } = db.useQuery({
    notes: {
      $: {
        where: { id: noteId },
      },
    },
  })

  const note = data?.notes?.[0] as Note | undefined

  return {
    note,
    isLoading,
    error,
  }
}

